// The app's QueryClient. Some errors change who the person is or what they may do,
// so /api/me is checked again and the shell follows:
//   401 (session ended) or NO_ORG: the route guard sends them to sign in or to create an org.
//   TRIAL_ENDED (a write after the free trial ended): the trial banner turns to read-only at once.
//   PAYMENT_DETAILS_REQUIRED (a write while the trial waits for payment details): back to adding them.
// Every other failed write (a save or a delete) is announced, so a failure is
// never silent (Marius round 5, SY1/AC1): ToastProvider shows it unless the
// screen already shows the error itself (a form's "There is a problem" box).
// A mutation that shows its own error elsewhere may pass meta: { silentError: true }.
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./client";
import { qk } from "./keys";
import { followNumberFormat } from "../lib/number-format";

/** Window event that ToastProvider turns into an error toast. detail: WriteFailure. */
export const WRITE_FAILED_EVENT = "convenor:write-failed";

export type WriteFailure = { message: string; code: string };

/** What the person reads when a write fails: the server's message, or a plain fallback. */
export function writeFailure(error: unknown): WriteFailure {
  if (error instanceof ApiError) return { message: error.message, code: error.code };
  return { message: "That did not work. Please try again.", code: "INTERNAL" };
}

/** Failures the shell answers by itself (sign-in page, read-only banner): no toast. */
const handledByShell = (error: unknown) =>
  error instanceof ApiError && (error.status === 401 || error.code === "NO_ORG" || error.code === "TRIAL_ENDED" || error.code === "PAYMENT_DETAILS_REQUIRED");

const changesMe = (error: unknown) =>
  error instanceof ApiError && (error.status === 401 || error.code === "NO_ORG" || error.code === "TRIAL_ENDED" || error.code === "PAYMENT_DETAILS_REQUIRED");

export function createAppQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.queryKey[0] === qk.me[0]) return;
        if (changesMe(error)) void client.invalidateQueries({ queryKey: qk.me });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (error instanceof ApiError && (error.code === "TRIAL_ENDED" || error.code === "PAYMENT_DETAILS_REQUIRED")) void client.invalidateQueries({ queryKey: qk.me });
        // Any other failed write: tell the person, also for buttons that have no form (a Remove link).
        if (handledByShell(error) || mutation.meta?.silentError) return;
        if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent<WriteFailure>(WRITE_FAILED_EVENT, { detail: writeFailure(error) }));
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
      },
    },
  });
  // R2-008: amounts in the signed-in person's number format.
  followNumberFormat(client);
  return client;
}
